import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { Button, Dropdown, Typography } from '@robinpowered/ui-kit';
import {
  FilterButton,
  FilterButtonContent,
  overlayPopupStyles,
} from '../styles';
import { useFilterOptionKeyboardNav } from '../hooks/useFilterOptionKeyboardNav';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faChairOffice,
  faChevronDown,
  faDoorOpen,
  faListUl,
  faUser,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import { GlobalSearchEntityType } from 'generated/graphql';
import {
  type CustomResourceIconConfig,
  fallbackCustomResourceIcon,
  genericCustomResourceIcon,
} from '../customResourceIcons';
import {
  isSameCategorySelection,
  type SearchCategorySelection,
} from '../types';

/** One selectable category, as supplied by globalSearchFilterOptions. */
export interface CategoryOptionRow {
  entityType: GlobalSearchEntityType;
  displayName: string;
  categoryTypeId?: string | null;
  /** Resolved icon for custom resource categories; built-ins have their own. */
  icon?: CustomResourceIconConfig;
}

interface CategoryFilterProps {
  /**
   * Options from globalSearchFilterOptions. While these load (or if they
   * fail), the built-in types are offered so the filter always works.
   */
  categoryOptions: CategoryOptionRow[];
  /** True while the filter options are still loading; shows a hint that more
   * categories (custom resources) may still arrive. */
  loading?: boolean;
  selectedCategory: SearchCategorySelection | null;
  onChange: (category: SearchCategorySelection | null) => void;
}

const BUILT_IN_FALLBACK_OPTIONS: Omit<CategoryOptionRow, 'displayName'>[] = [
  { entityType: GlobalSearchEntityType.User },
  { entityType: GlobalSearchEntityType.Space },
  { entityType: GlobalSearchEntityType.Desk },
];

// Same icons the corresponding result rows use.
const BUILT_IN_ICONS: Partial<Record<GlobalSearchEntityType, IconDefinition>> =
  {
    [GlobalSearchEntityType.User]: faUser,
    [GlobalSearchEntityType.Space]: faDoorOpen,
    [GlobalSearchEntityType.Desk]: faChairOffice,
  };

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categoryOptions,
  loading = false,
  selectedCategory,
  onChange,
}) => {
  const { t } = useTranslation('globalNavigation', {
    keyPrefix: 'searchOverlay',
  });
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Built-in types keep their translated labels; custom resource categories
  // are org-defined names and render as supplied by the backend.
  const builtInLabels: Partial<Record<GlobalSearchEntityType, string>> =
    useMemo(
      () => ({
        [GlobalSearchEntityType.User]: t('categoryFilter.people'),
        [GlobalSearchEntityType.Space]: t('categoryFilter.spaces'),
        [GlobalSearchEntityType.Desk]: t('categoryFilter.desks'),
      }),
      [t]
    );

  const options: {
    selection: SearchCategorySelection;
    label: string;
    icon?: CustomResourceIconConfig;
  }[] = useMemo(() => {
    const rows: CategoryOptionRow[] =
      categoryOptions.length > 0
        ? categoryOptions
        : BUILT_IN_FALLBACK_OPTIONS.map((option) => ({
            ...option,
            displayName: builtInLabels[option.entityType] ?? option.entityType,
          }));

    return rows.map((row) => {
      const label =
        row.categoryTypeId == null
          ? builtInLabels[row.entityType] ?? row.displayName
          : row.displayName;
      return {
        selection: {
          entityType: row.entityType,
          categoryTypeId: row.categoryTypeId ?? null,
          displayName: label,
        },
        label,
        icon:
          row.categoryTypeId == null
            ? {
                fontAwesome:
                  BUILT_IN_ICONS[row.entityType] ?? genericCustomResourceIcon,
              }
            : row.icon ?? fallbackCustomResourceIcon,
      };
    });
  }, [categoryOptions, builtInLabels]);

  // Close the dropdown and return focus to the trigger so Tab continues to the
  // next control instead of falling back to the start of the modal.
  const closeDropdown = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const selectCategory = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option) {
        return;
      }
      onChange(
        isSameCategorySelection(option.selection, selectedCategory)
          ? null
          : option.selection
      );
      closeDropdown();
    },
    [options, selectedCategory, onChange, closeDropdown]
  );

  const { activeIndex } = useFilterOptionKeyboardNav({
    open,
    optionCount: options.length,
    onSelect: selectCategory,
    onClose: closeDropdown,
  });

  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    const optionElements =
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    optionElements?.[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectedLabel = options.find((option) =>
    isSameCategorySelection(option.selection, selectedCategory)
  )?.label;

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      styles={overlayPopupStyles}
      popupRender={() => (
        <FilterPanel role="group" aria-label={t('categoryFilter.groupLabel')}>
          <CategoryList ref={listRef} role="listbox">
            {options.map((option, index) => {
              const isSelected = isSameCategorySelection(
                option.selection,
                selectedCategory
              );
              const isActive = index === activeIndex;
              const { fontAwesome, customComponent: CustomIcon } =
                option.icon ?? {};
              return (
                <CategoryOption
                  key={`${option.selection.entityType}-${
                    option.selection.categoryTypeId ?? 'builtin'
                  }`}
                  type="text"
                  role="option"
                  aria-selected={isSelected}
                  data-active={isActive}
                  onClick={() => selectCategory(index)}
                >
                  <OptionIcon aria-hidden="true">
                    {CustomIcon ? (
                      <CustomIcon />
                    ) : fontAwesome ? (
                      <FontAwesomeIcon icon={fontAwesome} />
                    ) : null}
                  </OptionIcon>
                  {option.label}
                </CategoryOption>
              );
            })}
          </CategoryList>
          {loading && (
            <PanelMessage role="status">
              {t('categoryFilter.loading')}
            </PanelMessage>
          )}
        </FilterPanel>
      )}
    >
      <FilterButton ref={triggerRef} type="text">
        <FilterButtonContent>
          <FontAwesomeIcon icon={faListUl} />
          <span>
            {selectedLabel
              ? t('categoryFilter.triggerSelected', { category: selectedLabel })
              : t('category')}
          </span>
          <FontAwesomeIcon icon={faChevronDown} />
        </FilterButtonContent>
      </FilterButton>
    </Dropdown>
  );
};

const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
  padding: 8px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
`;

/* Orgs can define any number of custom resource categories; cap and scroll
   the list so the popup matches the buildings dropdown's 280x296 footprint
   (no search input here, so the list gets its 40px). */
const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
`;

const CategoryOption = styled(Button)`
  justify-content: flex-start;
  width: 100%;
  min-height: 36px;
  text-align: left;

  &[aria-selected='true'] {
    background: #f5f5f5;
    font-weight: 600;
  }

  &&[data-active='true'] {
    background: #e6f4ff;
  }
`;

const OptionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.72);
`;

const PanelMessage = styled(Typography.Text)`
  padding: 8px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 13px;
`;
